/* ── CommandPalette ───────────────────────────────────────────
   Quick-action dropdown that appears below the query input when
   Cmd+K is pressed and the input is empty.

   Features:
   - Fuzzy filter over actions
   - Templates section (shown after divider, or exclusively when
     the filter starts with "/")
   - Keyboard navigation (↑/↓/Enter/Esc)
   ────────────────────────────────────────────────────────────── */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { QueryTemplate } from '../api';

export interface PaletteAction {
  id: string;
  label: string;
  icon: string;
  /** If a shortcut key combo exists, show it */
  shortcut?: string;
}

export interface PaletteTemplate {
  id: string;
  label: string;
  icon: string;
  domain: string;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);
const mod = isMac ? '⌘' : 'Ctrl';

const isAdmin = () => {
  try { return localStorage.getItem('standards-user-role') === 'admin'; } catch { return false; }
};

const BASE_ACTIONS: PaletteAction[] = [
  { id: 'new-chat',        label: 'New chat',              icon: '✨', shortcut: `${mod}+N` },
  { id: 'settings',        label: 'Open settings',         icon: '⚙️', shortcut: `${mod}+,` },
  { id: 'bookmarks',       label: 'View bookmarks',        icon: '🔖', shortcut: `${mod}+B` },
  { id: 'highlights',      label: 'View highlights',       icon: '✏️', shortcut: `${mod}+H` },
  { id: 'corpus',          label: 'Browse corpus',         icon: '📚' },
  { id: 'compare',         label: 'Version Compare',       icon: '⚖️', shortcut: `${mod}+E` },
  { id: 'report',          label: 'Build report',          icon: '📄' },
  { id: 'manual-builder',  label: 'Compliance Copy Builder', icon: '📋' },
  { id: 'manual-review',   label: 'Manual Review — Check PDF Compliance Gaps', icon: '🔍' },
  { id: 'corpus-updates',  label: 'Corpus updates',        icon: '🔔' },
  { id: 'checklist',       label: 'Toggle checklist mode', icon: '☑️' },
  { id: 'history',         label: 'Query history',          icon: '📋' },
  { id: 'resources',       label: 'External resources',    icon: '🔗' },
  { id: 'shortcuts',       label: 'Keyboard shortcuts',    icon: '⌨️', shortcut: `${mod}+/` },
];

const ADMIN_ACTIONS: PaletteAction[] = [
  { id: 'dashboard',       label: 'Open dashboard',        icon: '📊' },
];

function getDefaultActions(): PaletteAction[] {
  return isAdmin() ? [...BASE_ACTIONS, ...ADMIN_ACTIONS] : BASE_ACTIONS;
}

const TEMPLATES: PaletteTemplate[] = [
  { id: 'tpl-earthing',    label: 'Earthing requirements for residential installations',    icon: '⚡', domain: 'Electrical' },
  { id: 'tpl-fire',        label: 'NCC fire resistance requirements for Class 2 buildings', icon: '🔥', domain: 'NCC' },
  { id: 'tpl-clause',      label: 'AS/NZS 3000 clause lookup',                              icon: '🔧', domain: 'Electrical' },
  { id: 'tpl-backflow',    label: 'Plumbing backflow prevention requirements',               icon: '🚿', domain: 'Plumbing' },
  { id: 'tpl-access',      label: 'DDA access requirements for public buildings',            icon: '♿', domain: 'Access' },
  { id: 'tpl-structural',  label: 'Wind load requirements for residential structures',       icon: '🏗️', domain: 'Structural' },
  { id: 'tpl-watermark',   label: 'WaterMark certification requirements',                    icon: '💧', domain: 'WaterMark' },
  { id: 'tpl-consumer',    label: 'Product recall obligations under ACL',                    icon: '📦', domain: 'Consumer' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onAction: (actionId: string) => void;
  /** Fill the query input with a template string */
  onFillInput?: (text: string) => void;
  /** Anchor element to position relative to */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** Saved query templates from D1 */
  savedTemplates?: QueryTemplate[];
  /** Delete a saved template */
  onDeleteTemplate?: (id: string) => void;
}

export default function CommandPalette({ open, onClose, onAction, onFillInput, anchorRef, savedTemplates = [], onDeleteTemplate }: Props) {
  const [filter, setFilter] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /* Determine if we're in template-only mode (filter starts with "/") */
  const isTemplateMode = filter.startsWith('/');
  const templateQuery = isTemplateMode ? filter.slice(1).toLowerCase().trim() : filter.toLowerCase().trim();

  const filteredActions = useMemo(() => {
    if (isTemplateMode) return []; // Hide actions when filtering templates
    const actions = getDefaultActions();
    if (!filter.trim()) return actions;
    const q = filter.toLowerCase();
    return actions.filter(
      (a) => a.label.toLowerCase().includes(q) || a.id.includes(q),
    );
  }, [filter, isTemplateMode]);

  const filteredTemplates = useMemo(() => {
    if (isTemplateMode) {
      if (!templateQuery) return TEMPLATES;
      return TEMPLATES.filter(
        (t) => t.label.toLowerCase().includes(templateQuery) || t.domain.toLowerCase().includes(templateQuery),
      );
    }
    if (!filter.trim()) return TEMPLATES; // Show all templates when no filter
    const q = filter.toLowerCase();
    return TEMPLATES.filter(
      (t) => t.label.toLowerCase().includes(q) || t.domain.toLowerCase().includes(q),
    );
  }, [filter, isTemplateMode, templateQuery]);

  /* Saved (D1) templates filtered by current query */
  const filteredSavedTemplates = useMemo(() => {
    if (!savedTemplates.length) return [];
    const q = isTemplateMode ? templateQuery : filter.toLowerCase().trim();
    if (!q) return savedTemplates;
    return savedTemplates.filter(
      (t) => t.name.toLowerCase().includes(q) || t.query.toLowerCase().includes(q),
    );
  }, [savedTemplates, filter, isTemplateMode, templateQuery]);

  /* Combined list for keyboard navigation */
  const allItems = useMemo(() => {
    const items: Array<{
      type: 'action' | 'divider' | 'template' | 'saved-divider' | 'saved-template';
      data?: PaletteAction | PaletteTemplate | QueryTemplate;
    }> = [];
    filteredActions.forEach((a) => items.push({ type: 'action', data: a }));
    // Saved templates section (D1)
    if (filteredSavedTemplates.length > 0) {
      items.push({ type: 'saved-divider' });
      filteredSavedTemplates.forEach((t) => items.push({ type: 'saved-template', data: t }));
    }
    // Hardcoded templates section
    if (filteredTemplates.length > 0) {
      if (filteredActions.length > 0 || filteredSavedTemplates.length > 0) {
        items.push({ type: 'divider' });
      }
      filteredTemplates.forEach((t) => items.push({ type: 'template', data: t }));
    }
    return items;
  }, [filteredActions, filteredTemplates, filteredSavedTemplates]);

  /* Selectable items only (skip dividers) */
  const selectableItems = useMemo(
    () => allItems.filter((item) => item.type !== 'divider' && item.type !== 'saved-divider'),
    [allItems],
  );

  /* Reset filter + index when opened */
  useEffect(() => {
    if (open) {
      setFilter('');
      setActiveIdx(0);
      // Small delay to ensure the palette is rendered before focusing
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [open]);

  /* Clamp activeIdx to selectable length */
  useEffect(() => {
    if (activeIdx >= selectableItems.length) {
      setActiveIdx(Math.max(0, selectableItems.length - 1));
    }
  }, [selectableItems.length, activeIdx]);

  /* Execute the currently selected item */
  const executeItem = useCallback(
    (item: (typeof selectableItems)[number]) => {
      if (item.type === 'action') {
        onAction((item.data as PaletteAction).id);
        onClose();
      } else if (item.type === 'template') {
        onFillInput?.((item.data as PaletteTemplate).label);
        onClose();
      } else if (item.type === 'saved-template') {
        onFillInput?.((item.data as QueryTemplate).query);
        onClose();
      }
    },
    [onAction, onClose, onFillInput],
  );

  /* Keyboard navigation */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, selectableItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (selectableItems[activeIdx]) {
          executeItem(selectableItems[activeIdx]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [selectableItems, activeIdx, executeItem, onClose],
  );

  /* Close on click outside */
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const el = listRef.current;
      if (el && !el.contains(e.target as Node)) {
        onClose();
      }
    };
    // Use a timeout to avoid catching the same click that opened it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handler);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handler);
    };
  }, [open, onClose]);

  if (!open) return null;

  /* Map selectableItems index to rendered position for active tracking */
  let selectableIdx = 0;

  return (
    <div className="cp-container" ref={listRef} role="listbox" aria-label="Quick actions">
      <div className="cp-search-row">
        <input
          ref={inputRef}
          className="cp-search"
          type="text"
          value={filter}
          onChange={(e) => { setFilter(e.target.value); setActiveIdx(0); }}
          onKeyDown={handleKeyDown}
          placeholder={isTemplateMode ? 'Filter templates…' : 'Type a command… (/ for templates)'}
          aria-label="Filter commands"
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      {selectableItems.length === 0 ? (
        <div className="cp-empty">No matching {isTemplateMode ? 'templates' : 'actions'}</div>
      ) : (
        <div className="cp-list">
          {allItems.map((item, idx) => {
            if (item.type === 'divider') {
              return (
                <div key="divider" className="cp-divider" role="separator">
                  <span className="cp-section-label">Quick Templates</span>
                </div>
              );
            }

            if (item.type === 'saved-divider') {
              return (
                <div key="saved-divider" className="cp-divider" role="separator">
                  <span className="cp-section-label">Saved Templates</span>
                </div>
              );
            }

            const currentSelectableIdx = selectableIdx++;
            const isActive = currentSelectableIdx === activeIdx;

            if (item.type === 'action') {
              const action = item.data as PaletteAction;
              return (
                <button
                  key={action.id}
                  className={`cp-item ${isActive ? 'cp-item--active' : ''}`}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => { onAction(action.id); onClose(); }}
                  onMouseEnter={() => setActiveIdx(currentSelectableIdx)}
                >
                  <span className="cp-item-icon">{action.icon}</span>
                  <span className="cp-item-label">{action.label}</span>
                  {action.shortcut && (
                    <span className="cp-item-shortcut">{action.shortcut}</span>
                  )}
                </button>
              );
            }

            if (item.type === 'saved-template') {
              const tpl = item.data as QueryTemplate;
              return (
                <div
                  key={tpl.id}
                  className={`cp-item cp-item--saved ${isActive ? 'cp-item--active' : ''}`}
                  role="option"
                  aria-selected={isActive}
                  onMouseEnter={() => setActiveIdx(currentSelectableIdx)}
                >
                  <button
                    className="cp-item-saved-main"
                    onClick={() => { onFillInput?.(tpl.query); onClose(); }}
                    type="button"
                    aria-label={`Use template: ${tpl.name}`}
                  >
                    <span className="cp-item-icon">📌</span>
                    <span className="cp-item-label">{tpl.name}</span>
                    <span className="cp-item-badge cp-item-badge--saved">Saved</span>
                  </button>
                  <button
                    className="cp-item-delete"
                    onClick={(e) => { e.stopPropagation(); onDeleteTemplate?.(tpl.id); }}
                    type="button"
                    aria-label={`Delete template: ${tpl.name}`}
                    title="Delete template"
                  >
                    ×
                  </button>
                </div>
              );
            }

            if (item.type === 'template') {
              const tpl = item.data as PaletteTemplate;
              return (
                <button
                  key={tpl.id}
                  className={`cp-item ${isActive ? 'cp-item--active' : ''}`}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => { onFillInput?.(tpl.label); onClose(); }}
                  onMouseEnter={() => setActiveIdx(currentSelectableIdx)}
                >
                  <span className="cp-item-icon">{tpl.icon}</span>
                  <span className="cp-item-label">{tpl.label}</span>
                  <span className="cp-item-badge">{tpl.domain}</span>
                </button>
              );
            }

            return null;
          })}
        </div>
      )}
    </div>
  );
}
